import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import { useT } from "./i18n";
import { askClaudeWithTools, phraseDailyPlan, extractStudyMinutes } from "./api";
import { computeDailyPlanWaterfall, parseMinutesFromText, MIN_STUDY_MINUTES, MAX_STUDY_MINUTES } from "./babiCheckins";
import { auth, saveReport, logBabiSessionMetrics } from "./firebase";
import { save, load, todayISO, fmtWpd } from "./utils";
import { BABI_TOOLS, executeBabiTool, LANDING_REFERENCE, FEATURE_SUMMARY, HARD_GUARDRAILS, SCRIPT_FIDELITY_NOTE } from "./babiKnowledge";
import { VOICE_LOW_BALANCE_BUFFER_SECONDS, shouldOfferVoiceOverage, hasVoiceLabAccess } from "./constants";
import { useVoiceTurn, TRANSCRIPT_GRACE_PERIOD_MS } from "./useVoiceTurn";

// Tool-result round trips per user message before giving up and returning
// whatever text Babi has produced so far. Generous enough for "look up a
// topic, then look up a grammar point, then answer" but bounded so a
// pathological loop can't run away.
const MAX_TOOL_ROUNDS = 4;

// Appended to the system prompt for the one turn where the learner asked something unrelated
// while the Daily Study Plan's "how much time do you have" question was still pending (see the
// pendingDailyPlan branch in send()) — answers the real question via the normal pipeline first,
// then re-asks for the time so the plan flow can still complete on a later turn.
const DAILY_PLAN_FOLLOWUP_NOTE = `

DAILY PLAN TIME STILL NEEDED: the learner hasn't yet told you how much time they have today, which
you need before you can put together today's study plan — they asked about something else instead.
Answer their message normally above using your usual roles, then add one short additional line at
the end asking again how much time they have today so you can build the plan. Keep it natural, not
a repeat of a fixed script.`;

// Instrumentation-only (2026-09 cost-sizing task, see AgentChat's sessionMetricsRef): a rough,
// keyword-based proxy for "this reply touched pricing/subscription/feature-support territory"
// — the closest signal available without full content classification, and specifically meant to
// estimate how often a future app_pricing_and_features-style tool would fire if FEATURE_SUMMARY/
// LANDING_REFERENCE were moved behind one. Deliberately approximate: matched against Babi's own
// reply text in-memory only, to increment a counter — the matched text itself is never stored
// (see the privacy note on sessionMetricsRef below). False positives/negatives are expected and
// fine for a sizing estimate; this is not meant to be a precise topic classifier.
const PRICING_FEATURE_KEYWORDS = /\b(pro|elite|subscription|trial|pricing|price|upgrade|downgrade|refund|cancel|pause|paddle|billing|invoice|voice minutes?|plan)\b/i;

// Babi's chat widget only reads a reply aloud when this is true. Turned off (2026-09,
// product decision): for THIS widget, speech-in/text-out is the intended shape —
// STT lets you ask by voice, but Babi answering out loud added nothing here and cost
// real Google TTS money on every voice turn for no benefit. The whole voice-reply
// pipeline (useVoiceTurn's speakReply, the textToSpeech API call, and the proxy's
// automatic Neural2-voice-selection logic in proxy/index.js's pickTtsVoice) is left
// fully working and untouched — it's reserved for the "Listen & Speak Lab" feature,
// which WILL want spoken replies. Flip this back to true (or have that feature pass
// ttsEnabled:true into useVoiceTurn) rather than rebuilding the pipeline from scratch.
const BABI_WIDGET_TTS_ENABLED = false;

// A2 (widget) deliberately uses a shorter, distinct silence backstop than the
// Lab's LAB_SILENCE_TIMEOUT_MS — quick clarification questions in the widget
// tolerate a snappier cutoff than a structured Lab conversation would. Not a raw
// amplitude/RMS threshold: this is the `redemptionMs` value fed to
// @ricky0123/vad-web's Silero-model VAD (passed into useVoiceTurn as
// silenceTimeoutMs) — how long the VAD waits after speech-negative frames begin
// before treating a turn as finished, not a fixed timer running independent of
// actual speech detection.
const WIDGET_SILENCE_TIMEOUT_MS = 1000;

// Isolates every occurrence of the user's own name within already-rendered
// message text so its direction resolves independently — otherwise a
// Latin-script name followed by punctuation (e.g. "Hello, Ali! 👋") can have
// that punctuation visually flip to the wrong side inside RTL (Farsi/Arabic)
// chat bubbles. Operates at render time so the stored message string (sent
// to the API as conversation history) is untouched.
function isolateName(content, name) {
  if (!name) return content;
  const parts = content.split(name);
  if (parts.length < 2) return content;
  const out = [parts[0]];
  for (let i = 1; i < parts.length; i++) out.push(<bdi key={i}>{name}</bdi>, parts[i]);
  return out;
}

// Same bidi problem as isolateName above, generalized to any Latin-script run —
// Babi frequently quotes a Turkish example phrase verbatim inside an RTL (Farsi/
// Arabic) reply, e.g. «Nasıl gidiyorsun?», and a trailing Latin "?" sitting right
// before more RTL text gets pulled to the wrong (left) side by the Unicode Bidi
// Algorithm unless the whole quoted run — punctuation included — is isolated as
// one unit. No RTL characters at all means no-op: a pure-Latin reply needs none
// of this, so skip the split rather than doing pointless work on every English turn.
const RTL_CHAR = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/;
const LATIN_RUN = /([A-Za-z0-9\u00C0-\u017F][A-Za-z0-9\u00C0-\u017F '’".,!?;:()-]*[A-Za-z0-9\u00C0-\u017F'".,!?;:()-]|[A-Za-z0-9\u00C0-\u017F])/;
function isolateLatinRuns(text, keyPrefix) {
  if (typeof text !== "string" || !RTL_CHAR.test(text)) return text;
  const parts = text.split(LATIN_RUN);
  if (parts.length < 2) return text;
  return parts.map((part, i) => (i % 2 === 1 ? <bdi key={`${keyPrefix}${i}`}>{part}</bdi> : part));
}

// Combines both isolations for a chat bubble's rendered content: isolateName first
// (a known, exact literal match), then isolateLatinRuns over whatever plain-string
// pieces remain — flattened into one child array React can render directly.
// Exported for reuse by the Listen & Speak Lab's bubbles (src/ListenSpeakLab.js).
export function renderChatContent(content, name) {
  const afterName = isolateName(content, name);
  const chunks = Array.isArray(afterName) ? afterName : [afterName];
  return chunks.flatMap((chunk, i) => (typeof chunk === "string" ? isolateLatinRuns(chunk, `l${i}-`) : chunk));
}

export function AgentChat({ profile, progress, cards, grammarProgress, completedTopics, selectedTopic, screen, baranAlert, onBabiAlertClear, pendingDailyPlan, onDailyPlanAnswered, onUpdateProfile, subscription, isAdminAccount, isSimpleViewer, onBuyVoiceMinutes }) {
  const { t, isRTL, lang } = useT();
  const langCode = (profile?.targetLanguage || "Turkish").toLowerCase();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  // A5: shown after Babi calls wrap_up_conversation and has finished her spoken sign-off.
  const [showWrapUpNudge, setShowWrapUpNudge] = useState(false);
  // A6: dismissible per-session, independent of showWrapUpNudge above — this is the earlier,
  // calmer "you're under 30 minutes" notice (Elite-only), not tied to any specific conversation
  // turn, so it has no natural "turn ended" moment to auto-hide at like showWrapUpNudge does.
  const [lowMinutesBannerDismissed, setLowMinutesBannerDismissed] = useState(false);
  const messagesRef = useRef(null);
  const pendingVoiceTextareaRef = useRef(null);
  const panelRef = useRef(null);
  const toggleRef = useRef(null);

  // Instrumentation-only (2026-09 cost-sizing task): accumulates per-session counts for the main
  // (non-daily-plan) conversation path, flushed once on unmount via logBabiSessionMetrics below.
  // A "session" here is one mount lifetime of AgentChat — the same boundary `messages` itself
  // already resets on (App.js mounts/unmounts this component via `showChrome && <AgentChat .../>`,
  // so navigating away and back starts a fresh mount, a fresh `messages` array, and a fresh one of
  // these). Refs, not state: nothing here needs to trigger a re-render, and a ref survives to the
  // unmount cleanup without racing React's async state batching. Deliberately holds NO message
  // content anywhere — only counts, tool names, and token numbers already returned by the API.
  const sessionStartRef = useRef(null);
  if (sessionStartRef.current === null) sessionStartRef.current = Date.now();
  const sessionMetricsRef = useRef({
    turnCount: 0,             // user turns that reached the main askClaudeWithTools path (the
                               // pendingDailyPlan short-circuit below never touches this — it
                               // doesn't use buildSystemPrompt/BABI_TOOLS, so it's out of scope
                               // for sizing THIS prompt's cost)
    apiCallCount: 0,          // total askClaudeWithTools calls, i.e. turnCount + every extra
                               // tool-round call — the gap between the two is the actual cost of
                               // tool-use round trips, not just whether MAX_TOOL_ROUNDS was hit
    toolRoundsPerTurn: [],    // one entry per turn: how many extra rounds (0..MAX_TOOL_ROUNDS)
                               // that turn actually consumed
    toolCallCounts: {},       // { [toolName]: timesCalled } across the whole session
    pricingKeywordReplyCount: 0, // see PRICING_FEATURE_KEYWORDS above
    apiCalls: [],             // [{ turnIndex, inputTokens, outputTokens }, ...] in call order —
                               // per-call (not per-user-total) so growth across a session's
                               // turns is visible, not just the session's grand total
  });

  // Flush on unmount only — a single write per session rather than one per turn, since nothing
  // here needs to be seen mid-session. turnCount === 0 (panel opened, nothing ever sent) is
  // skipped so this collection isn't dominated by zero-signal opens.
  useEffect(() => {
    return () => {
      // Intentional: this must read sessionMetricsRef.current's FINAL accumulated value at
      // unmount, not a snapshot copied when the effect first ran (which is what the rule's own
      // suggested fix would produce) — the whole point is capturing everything sent during the
      // session, right up to the moment it ends.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const m = sessionMetricsRef.current;
      if (m.turnCount === 0) return;
      logBabiSessionMetrics({
        startedAt: new Date(sessionStartRef.current).toISOString(),
        durationMs: Date.now() - sessionStartRef.current,
        turnCount: m.turnCount,
        apiCallCount: m.apiCallCount,
        toolRoundsPerTurn: m.toolRoundsPerTurn,
        toolCallCounts: m.toolCallCounts,
        pricingKeywordReplyCount: m.pricingKeywordReplyCount,
        apiCalls: m.apiCalls,
      });
    };
  }, []);

  // The voice I/O pipeline (VAD capture, STT, the A4 review window, TTS playback, voice-minutes
  // billing, the A5 mic mutex) lives in useVoiceTurn so the Listen & Speak Lab can drive the
  // exact same machine. The widget configures it to behave exactly as it always has: its own 1s
  // silence cutoff, TTS still gated off (BABI_WIDGET_TTS_ENABLED), background VAD preload on
  // panel open, and its full-persona buildSystemPrompt handed to send() via onSubmitTranscript.
  //
  // sendRef / buildSystemPromptRef are latest-ref indirections so useVoiceTurn's deferred
  // callbacks (the grace-period timeout, the "send now" button) always reach this render's
  // send()/buildSystemPrompt — neither of which can be defined before this hook call, since
  // send() closes over what the hook returns. send(voiceText) ignores the forwarded 2nd arg
  // (it already closes over buildSystemPrompt), so this is a no-op for the widget and a
  // convenience for the Lab.
  const sendRef = useRef(null);
  const buildSystemPromptRef = useRef(null);
  const handleVoiceSubmit = useCallback((...args) => sendRef.current?.(...args), []);
  const forwardedBuildSystemPrompt = useCallback((isVoiceTurn) => buildSystemPromptRef.current?.(isVoiceTurn), []);

  const {
    voiceStage, setVoiceStage,
    transcriptState, setTranscriptState,
    pendingVoiceText, setPendingVoiceText,
    pendingVoiceEditing, pendingReviewId,
    ttsNotice, setTtsNotice,
    micSupported, hasVoiceConsent, voiceEntitled, hasVoiceBalance, voiceRemainingSeconds,
    requestMic, handleMicClick,
    beginEditingPendingVoiceText, confirmPendingVoiceText, discardPendingVoiceText,
    speakReply,
  } = useVoiceTurn({
    profile, subscription, isAdminAccount, isSimpleViewer, onUpdateProfile,
    panelOpen: open,
    enablePreload: true,
    silenceTimeoutMs: WIDGET_SILENCE_TIMEOUT_MS,
    ttsEnabled: BABI_WIDGET_TTS_ENABLED,
    buildSystemPrompt: forwardedBuildSystemPrompt,
    onSubmitTranscript: handleVoiceSubmit,
    onVoiceCaptureStart: () => setShowWrapUpNudge(false),
  });

  // A6: Elite-only, becomes true once remaining balance drops under the 30-minute offer
  // threshold — independent of showWrapUpNudge's 75-second wrap-up trigger below (both can be
  // true at once near the very end of a conversation; they answer different questions).
  const canOfferVoiceOverage = shouldOfferVoiceOverage(voiceRemainingSeconds, profile, subscription, isAdminAccount, isSimpleViewer);
  // Dedup gate for the escalate_to_human tool: skip writing a second report
  // only if the model calls the tool again with essentially the same subject
  // (it re-flagging the same still-unresolved issue) — a genuinely new,
  // different issue raised later in the same chat session must still reach
  // Firestore. Keyed on normalized subject text rather than a single
  // session-wide boolean; confirmed via live testing that a boolean "one
  // report ever" gate silently drops real, distinct escalations (e.g. a
  // billing complaint raised after an earlier unrelated grammar-question
  // escalation) while still telling the user it was flagged. A ref (not
  // state) because updating it must never trigger a re-render.
  const escalatedSubjectsRef = useRef(new Set());
  // Failed-parse counter for the Daily Study Plan's "how much time do you have" question — after
  // a couple of genuinely unparseable replies, give up gracefully. A reply that's actually a
  // separate question/request (extractStudyMinutes' otherRequest flag) doesn't count against this
  // — it's routed to the normal pipeline (DAILY_PLAN_FOLLOWUP_NOTE) instead of burning an attempt.
  const dailyPlanAttemptsRef = useRef(0);

  // Picked once per language (not on every render) so it doesn't change
  // while the user is typing in the input box.
  const greetWord = useMemo(() => {
    const words = t("chat.greetWords");
    const arr = Array.isArray(words) ? words : [];
    return arr[Math.floor(Math.random() * arr.length)] || "";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // Some languages place the greeting word after the name rather than before it
  // (e.g. Persian "عزیز" is a postpositive adjective: "Hooman عزیز", not "عزیز Hooman").
  // chat.greetWordsAfterName (optional, locale-specific) lists which greetWords need that order.
  const greetedName = useMemo(() => {
    const name = profile?.name || "";
    const afterNameWords = t("chat.greetWordsAfterName");
    const isAfterName = Array.isArray(afterNameWords) && afterNameWords.includes(greetWord);
    return isAfterName ? `${name} ${greetWord}` : `${greetWord} ${name}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [greetWord, profile?.name, lang]);

  // Keep the conversation pinned to the bottom, and grow the transcript-review
  // textarea to fit its content, in ONE layout effect and in this exact order:
  //  1. resize the textarea (a <textarea>'s scrollHeight only reflects its content
  //     once that content is in the DOM, so it has to be measured here, not guessed)
  //  2. THEN scroll the messages box to its new bottom
  // so the scroll target is always computed against the box's final grown height —
  // never its pre-growth height, which used to leave the newly revealed bottom
  // (confirm/discard buttons, the rest of a long transcript) off-screen.
  //
  // Why a layout effect + direct scrollTop instead of scrollIntoView({behavior:
  // "smooth"}): smooth scroll is an async animation that can be left short if
  // layout shifts again mid-animation (the box growing, another VAD state update,
  // React 19 batching). Setting scrollTop synchronously before paint is
  // deterministic and flicker-free. We also scroll the container itself rather
  // than a marker element, so it can't accidentally scroll some other ancestor.
  useLayoutEffect(() => {
    const ta = pendingVoiceTextareaRef.current;
    if (ta) {
      ta.style.height = "auto";
      ta.style.height = `${ta.scrollHeight}px`;
    }
    const box = messagesRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [messages, loading, pendingVoiceText]);

  // Close on outside click, without letting the click that opened the panel
  // (tapping the toggle) immediately close it again, and without closing
  // mid text-selection drag that started inside the panel.
  useEffect(() => {
    if (!open) return;
    let mouseDownInside = false;

    const handleMouseDown = e => {
      mouseDownInside = !!(panelRef.current && panelRef.current.contains(e.target));
    };
    const handleClick = e => {
      if (toggleRef.current && toggleRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (mouseDownInside) return;
      setOpen(false);
    };

    const timerId = setTimeout(() => {
      document.addEventListener("mousedown", handleMouseDown);
      document.addEventListener("click", handleClick);
    }, 0);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("click", handleClick);
    };
  }, [open]);

  useEffect(() => {
    // Deferred while on placementTest: right after onboarding this component mounts
    // there first (showChrome doesn't exclude that screen), before the user has really
    // arrived in the app. Firing here — then possibly losing this state to an
    // install-prompt-screen unmount before the user reaches the dashboard — used to
    // burn the one-shot "welcomeShown" flag with nobody having seen the message.
    if (screen === "placementTest") return;
    if (!load("welcomeShown")) {
      setMessages([{ role: "assistant", content: t("chat.welcome", { name: profile?.name || "", targetLanguage: profile?.targetLanguage || "Turkish" }) }]);
      setOpen(true);
      save("welcomeShown", true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  useEffect(() => {
    if (baranAlert) {
      // Plain string (existing callers) or { key, params } for callers outside
      // I18nProvider's tree that can't call t() themselves (e.g. streak updates
      // computed in the top-level App component).
      const content = typeof baranAlert === "string" ? baranAlert : t(baranAlert.key, baranAlert.params);
      setMessages(prev => [...prev, { role: "assistant", content }]);
      setOpen(true);
      onBabiAlertClear?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baranAlert]);

  // isVoiceTurn (A3): appends a spoken-reply instruction block rather than building a
  // parallel prompt — confirmed via investigation that nothing in the existing prompt
  // (here or in babiKnowledge.js) already tells Babi to avoid markdown, and the chat
  // bubbles render replies as raw text with no markdown parser, so unstyled markdown
  // syntax (**bold**, "- item" bullets) would otherwise be read aloud literally by TTS.
  const buildSystemPrompt = (isVoiceTurn) => {
    const lang = profile?.baseLanguage || "English";
    const today = todayISO();

    const dueToday = (cards || []).filter(c =>
      (!c.d1Mastered && c.due <= today) || (c.d2 && !c.d2Mastered && c.d2.due <= today)
    ).length;
    const dist = (cards || []).reduce((a, c) => {
      const iv = c.interval || 0;
      if (!c.stability || iv < 3) a[0]++;
      else if (iv < 21) a[1]++;
      else if (iv < 90) a[2]++;
      else a[3]++;
      return a;
    }, [0, 0, 0, 0]);
    const totalCards = (cards || []).length;

    const userState = `
USER'S CURRENT STATE:
- Name: ${profile?.name || "unknown"}
- Base language: ${lang}
- Words learned: ${progress?.wordsLearned || 0} / ${profile?.targetWords || 3000}
- Lessons completed: ${progress?.lessonsCompleted || 0}
- Study streak: ${progress?.streak || 0} days
- Total study time: ${Math.round((progress?.totalMinutes || 0) / 60 * 10) / 10} hours
- Flashcards in deck: ${totalCards} total | Due today: ${dueToday}
- Memory breakdown: In Acquisition ${dist[0]} | Rooted ${dist[1]} | Fluent ${dist[2]} | Deeply Retained ${dist[3]}
- Current topic: ${selectedTopic?.topic || "none"} (Topic ${selectedTopic?.order || "?"}, ${selectedTopic?.words?.length || 0} words)
- Course goal: ${profile?.targetWords || 3000} words in ${profile?.courseLengthDays || 180} days (${fmtWpd(profile?.wordsPerDay || 0)} words/day)`.trim();

    return `You are Babi, a friendly Turkish language tutor and app assistant for "Speafy."
The learner's name is ${profile?.name || "there"} and their base language is ${lang}.
IMPORTANT: Always respond in ${lang} unless the learner writes to you in another language.

${SCRIPT_FIDELITY_NOTE}

${HARD_GUARDRAILS}

YOUR TWO ROLES:
1. Turkish language tutor — grammar, vocabulary, pronunciation, culture, language learning.
2. Product/technical support — how Speafy's features work, and troubleshooting. You may only
   ever suggest USER-FACING troubleshooting steps: refreshing the page, logging out and back
   in, checking their internet connection, updating the app/browser. Never reference a specific
   service, error type, database, config file, or any other implementation detail — even if you
   think it would be more helpful. If you don't know the real cause of a problem, say so plainly
   and offer to escalate rather than guessing at a technical explanation.
Use the APP KNOWLEDGE and LANDING SITE KNOWLEDGE sections below for role 2, and the
lookup_lesson_topic / lookup_grammar_point tools when a question needs a specific lesson's
words or a specific grammar rule you're not already confident about.
CURRICULUM/GRAMMAR LAB ACCURACY: when you call lookup_lesson_topic or lookup_grammar_point,
answer using ONLY the words/meanings/examples/forms the tool actually returned for that specific
lesson or grammar point — do not add extra example words or sentences of your own beyond what it
gave you, even correct ones, since these are meant to be the app's exact reference content, not
improvised. If the tool's result doesn't cover what the user asked, quietly fall back to a good
general answer rather than inventing something to fill the gap. This restriction is specific to
answering about that named lesson/grammar point — general Turkish tutoring questions not tied to
a specific lesson/grammar point are unaffected and may freely draw on your own knowledge as usual.
Never narrate your own process — do not say things like "I looked in the lesson," "I checked the
current topic," "I couldn't find that in the lesson, so here's a general answer," or similar. Just
give the best answer directly, as if you simply knew it, with no mention of tools, lookups, or
sources. Only name the specific lesson/topic/source if the user explicitly asks where the
information came from or asks you to cite it.
For anything outside both roles, politely decline in one sentence and redirect.
You cannot change any app settings yourself — for changes direct the user to the Goals page (target language, vocabulary/course goals, base language) or the Settings page (display name, subscription, data, privacy, appearance/dark mode/text size, account).
Keep answers concise and friendly.

SATISFACTION CHECK — MANDATORY:
After every response you give (whether it answers a question or politely declines), end with a single short line asking if the answer was helpful. Keep it natural and brief — one sentence, e.g. "Did that answer your question?" or "Was that helpful?" Vary the phrasing so it doesn't feel robotic.

ESCALATION RULE — MANDATORY, NOT OPTIONAL:
Escalate the moment ANY of these are true: the user explicitly asks for a human or for support;
the user's reply to your satisfaction check is negative or unhelpful; their question is outside
your knowledge; or you cannot resolve their issue after one follow-up attempt.
The moment any of those are true, you MUST call the escalate_to_human tool in that same turn with
a short subject and the matching reason code. This is a required action, not a suggestion —
writing an apology in plain text is NEVER a substitute for actually calling the tool, and you must
call it even if you also plan to write a reply that already sounds like an escalation.
In the SAME turn, also write a brief, natural, human-readable reply (translated into ${lang}) that
tells the user you've flagged this for the team and that they'll receive an answer by email
(typically within 2 business days), and mentions they can also reach support@speafy.com directly
in the meantime. It does not need to be a fixed template — a short, warm sentence covering those
points is enough.
Only call escalate_to_human once per distinct issue — do not call it again for the SAME
still-unresolved issue you already escalated earlier in this conversation, unless the user
explicitly asks again. If a genuinely different, separate issue comes up later in the same
conversation, escalate that one too — each real issue must reach the team, not just the first.

If YOU are the one who offered to escalate (e.g. "would you like me to flag this to the team?")
and the user's next message is any affirmative confirmation (yes/sure/please/ok/etc, in any
supported language), treat that confirmation as sufficient on its own. Call escalate_to_human in
that same turn with reason "escalation_offer_confirmed" and a natural, brief reply confirming the
report has been filed. Do not wait for a more explicit request, and do not respond with a plain
apology — a bare affirmative reply to your own offer IS the trigger.

${userState}

${FEATURE_SUMMARY}

${LANDING_REFERENCE}${isVoiceTurn ? `

SPOKEN REPLY MODE — this reply will be read aloud by text-to-speech, not displayed as text:
- Never use markdown — no **bold**, no bullet or numbered lists, no headers. Plain spoken
  sentences only, exactly as you'd say them out loud.
- Keep it shorter than a typical written answer — a natural spoken-length response, not a
  comprehensive written explanation.
- Everything else above still applies unchanged (roles, escalation rules, satisfaction
  check, language) — this only changes formatting and length, not content or behavior.` : ""}${
      isVoiceTurn && voiceRemainingSeconds <= VOICE_LOW_BALANCE_BUFFER_SECONDS ? `

LOW VOICE BALANCE: only about ${Math.max(0, Math.round(voiceRemainingSeconds))} seconds of voice
conversation remain this cycle. Call the wrap_up_conversation tool in THIS turn and end the
conversation gracefully — a warm, simple sign-off, nothing technical. Never mention minutes,
balance, running out, or anything about the app's mechanics to the user; that would break the
tutor persona. Do not offer or mention buying more time yourself — a separate UI element handles
that after you've signed off.` : ""
    }`;
  };

  const send = async (voiceText) => {
    const isVoiceTurn = voiceText !== undefined;
    const text = (isVoiceTurn ? voiceText : input).trim();
    if (!text || loading) return;
    const userMsg = { role: "user", content: text, ...(isVoiceTurn ? { fromVoice: true } : {}) };
    const history = [...messages, userMsg];
    setMessages(history);
    if (!isVoiceTurn) setInput("");
    setLoading(true);
    if (isVoiceTurn) setVoiceStage("replying");

    // Set below when pendingDailyPlan finds the reply is a genuine unrelated question/request
    // rather than a (possibly vague) attempt at the time question — routes this turn into the
    // normal pipeline below instead of returning early, so the real question gets answered.
    let dailyPlanFollowUp = false;

    if (pendingDailyPlan) {
      let minutes = parseMinutesFromText(text);
      let otherRequest = false;
      if (minutes == null || minutes <= 0) {
        // The local parser only understands English words/Western digits — fall back to
        // Haiku for non-English phrasing or native-script numerals, and to check whether this
        // is actually an unrelated question, before giving up.
        try {
          const extracted = await extractStudyMinutes(text, profile?.baseLanguage || "English");
          minutes = extracted?.minutes ?? null;
          otherRequest = !!extracted?.otherRequest;
        } catch { minutes = null; }
      }

      if (otherRequest) {
        // Don't burn a clarify attempt on this — fall through into the normal pipeline below
        // (DAILY_PLAN_FOLLOWUP_NOTE) so Babi actually answers it. pendingDailyPlan stays true,
        // so a later reply is still checked against this same flow.
        dailyPlanAttemptsRef.current = 0;
        dailyPlanFollowUp = true;
      } else {
        // A4: all three exit paths below converge on a single replyToSpeak + a single
        // setMessages/speakReply call, rather than each path duplicating its own (the
        // previous shape had two early `return`s mid-try, each with its own setMessages
        // call, which would have meant three separate speakReply() call sites to add).
        let replyToSpeak;
        try {
          // Enforced here regardless of which parser produced the number — the Haiku fallback is
          // also instructed to reject unrealistic amounts (api.js's extractStudyMinutes), but this
          // deterministic check is the real guarantee, e.g. against "18 hours" parsed correctly by
          // the local regex (hourMatch) but still not a sane daily study session.
          const outOfRange = minutes != null && (minutes < MIN_STUDY_MINUTES || minutes > MAX_STUDY_MINUTES);
          if (minutes == null || minutes <= 0 || outOfRange) {
            dailyPlanAttemptsRef.current += 1;
            if (dailyPlanAttemptsRef.current >= 2) {
              dailyPlanAttemptsRef.current = 0;
              onDailyPlanAnswered?.();
              replyToSpeak = t("chat.dailyPlanGiveUp");
            } else {
              replyToSpeak = t(outOfRange ? "chat.dailyPlanUnreasonable" : "chat.dailyPlanClarify");
            }
          } else {
            dailyPlanAttemptsRef.current = 0;
            const hasListenSpeakAccess = hasVoiceLabAccess(profile, subscription, isAdminAccount, isSimpleViewer);
            const plan = computeDailyPlanWaterfall(profile, progress, cards, grammarProgress, minutes, todayISO(), completedTopics, hasListenSpeakAccess);
            try {
              replyToSpeak = await phraseDailyPlan(plan, profile?.baseLanguage || "English");
            } catch (_) {
              replyToSpeak = t("chat.dailyPlanFallback");
            }
            onDailyPlanAnswered?.();
          }
          setMessages([...history, { role: "assistant", content: replyToSpeak }]);
          if (isVoiceTurn && BABI_WIDGET_TTS_ENABLED) await speakReply(replyToSpeak);
        } finally {
          setLoading(false);
          if (isVoiceTurn) setVoiceStage("idle");
        }
        return;
      }
    }

    try {
      const system = buildSystemPrompt(isVoiceTurn) + (dailyPlanFollowUp ? DAILY_PLAN_FOLLOWUP_NOTE : "");
      let convo = history.map(m => ({ role: m.role, content: m.content }));

      // Instrumentation-only: this turn's slot in sessionMetricsRef, and a tiny helper so both
      // call sites below (the initial call and the in-loop tool-round call) record the same way
      // without duplicating the bookkeeping.
      const sm = sessionMetricsRef.current;
      sm.turnCount++;
      const turnIndex = sm.turnCount;
      const recordApiCall = r => {
        sm.apiCallCount++;
        sm.apiCalls.push({
          turnIndex,
          inputTokens: r.usage?.input_tokens || 0,
          outputTokens: r.usage?.output_tokens || 0,
        });
      };

      let response = await askClaudeWithTools(convo, system, BABI_TOOLS);
      recordApiCall(response);
      let escalation = null;
      let wrapUpRequested = false;
      let rounds = 0;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const blocks = response.content || [];
        // Instrumentation-only: tally every tool Babi actually invoked this round, by name —
        // the closest available signal for real topic mix without full content classification.
        blocks.filter(b => b.type === "tool_use").forEach(b => {
          sm.toolCallCounts[b.name] = (sm.toolCallCounts[b.name] || 0) + 1;
        });
        const dataToolUses = blocks.filter(b => b.type === "tool_use" && b.name !== "escalate_to_human" && b.name !== "wrap_up_conversation");
        const escalateUse = blocks.find(b => b.type === "tool_use" && b.name === "escalate_to_human");
        const wrapUpUse = blocks.find(b => b.type === "tool_use" && b.name === "wrap_up_conversation");
        if (escalateUse && !escalation) escalation = escalateUse.input;
        if (wrapUpUse) wrapUpRequested = true;

        if (dataToolUses.length === 0 || rounds >= MAX_TOOL_ROUNDS) break;

        const toolResults = dataToolUses.map(tu => ({
          type: "tool_result",
          tool_use_id: tu.id,
          content: JSON.stringify(executeBabiTool(tu.name, tu.input, langCode)),
        }));
        convo = [...convo, { role: "assistant", content: blocks }, { role: "user", content: toolResults }];
        rounds++;
        response = await askClaudeWithTools(convo, system, BABI_TOOLS);
        recordApiCall(response);
      }
      // Instrumentation-only: how many extra rounds this turn actually consumed (0..MAX_TOOL_ROUNDS).
      sm.toolRoundsPerTurn.push(rounds);

      const replyText = (response.content || [])
        .filter(b => b.type === "text")
        .map(b => b.text)
        .join("") || (escalation ? t("chat.escalationFallback") : t("chat.error"));

      // Instrumentation-only: rough keyword proxy for "this reply touched pricing/feature-support
      // territory" — see PRICING_FEATURE_KEYWORDS's comment. Tested in-memory only; the reply text
      // itself is never written to sessionMetricsRef or Firestore.
      if (PRICING_FEATURE_KEYWORDS.test(replyText)) sm.pricingKeywordReplyCount++;

      setMessages([...history, { role: "assistant", content: replyText }]);

      // subject is schema-"required" but not guaranteed non-empty in practice (the model can emit
      // subject: "", or a long multi-round tool call chain can truncate it against max_tokens) — a
      // falsy key must never fall through to "dedup disabled", so an empty subject buckets by reason
      // instead of by itself, still deduping repeats without silently allowing unlimited re-fires.
      const rawSubjectKey = (escalation?.subject || "").trim().toLowerCase();
      const escalationKey = escalation ? (rawSubjectKey || `no-subject:${escalation.reason || "unspecified"}`) : "";
      if (escalation && !escalatedSubjectsRef.current.has(escalationKey)) {
        escalatedSubjectsRef.current.add(escalationKey);
        const transcript = [...history, { role: "assistant", content: replyText }]
          .map(m => `${m.role === "user" ? "User" : "Babi"}: ${m.content}`)
          .join("\n\n");
        saveReport({
          reportType: "babi-escalation",
          subject: escalation.subject || "Babi chat escalation",
          escalationReason: escalation.reason || "unspecified",
          message: transcript,
          baseLanguage: profile?.baseLanguage || "English",
          needsReview: true,
          createdAt: new Date().toISOString(),
          uid: auth?.currentUser?.uid || null,
          status: "open",
        }).catch(() => {
          // Best-effort — a failed report write must never surface as a
          // visible error on top of the reply the user already received.
        });
      }

      if (isVoiceTurn && BABI_WIDGET_TTS_ENABLED) await speakReply(replyText);
      // Shown only after Babi's spoken sign-off has actually finished playing — matches
      // the master plan's requirement that the "buy more hours" prompt is a separate UI
      // element appearing after she signs off, never something layered mid-goodbye.
      if (wrapUpRequested) setShowWrapUpNudge(true);
    } catch (_) {
      setMessages([...history, { role: "assistant", content: t("chat.error") }]);
    } finally {
      setLoading(false);
      // Single place voiceStage returns to 'idle' for a voice turn — whether the turn
      // ended in a real reply + speech, a text-only fallback (speakReply never throws
      // itself, it always resolves into a ttsNotice instead), or the outer catch above.
      if (isVoiceTurn) setVoiceStage("idle");
    }
  };

  // Point useVoiceTurn's latest-ref indirections at this render's closures (see the
  // sendRef/buildSystemPromptRef comment at the hook call). Runs after every render;
  // the deferred voice callbacks only ever read `.current`, never a captured value.
  useEffect(() => {
    sendRef.current = send;
    buildSystemPromptRef.current = buildSystemPrompt;
  });

  return (
    <div className="agent-chat-wrap">
      {open && (
        <div className="agent-chat-panel" ref={panelRef}>
          <div className="agent-chat-header">
            <img src="/babi%20fantasy2.jpg?v=2" style={{width:32,height:32,borderRadius:"50%",objectFit:"cover"}} alt="Babi" />
            <strong>Babi</strong>
            <span style={{fontSize:"0.75rem",opacity:0.7,marginLeft:4}}>· {t("chat.subtitle")}</span>
            <button className="chat-close-btn" onClick={() => setOpen(false)}>✕</button>
          </div>
          <div className="agent-chat-messages" ref={messagesRef}>
            {voiceStage === "consent" ? (
              <div className="voice-consent-card" dir="auto">
                <p className="voice-consent-title">{t("chat.voiceConsentTitle")}</p>
                <p className="voice-consent-body">{t("chat.voiceConsentBody")}</p>
                <a className="voice-consent-privacy-link" href="https://speafy.com/privacy" target="_blank" rel="noopener noreferrer">{t("chat.voiceConsentPrivacyLink")}</a>
                <div className="voice-consent-actions">
                  <button className="voice-consent-allow" onClick={() => requestMic(true)}>{t("chat.voiceConsentAllow")}</button>
                  <button className="voice-consent-decline" onClick={() => setVoiceStage("denied")}>{t("chat.voiceConsentDecline")}</button>
                </div>
              </div>
            ) : (
              <>
                {messages.length === 0 && (
                  <div className="chat-empty-msg" dir="auto" style={{textAlign: isRTL ? "right" : "left"}}>{isolateName(t("chat.greeting", { greet: greetWord, name: profile?.name || "", greetedName }), profile?.name)}</div>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`chat-bubble ${m.role}`} dir="auto">
                    {m.fromVoice && <span className="chat-bubble-voice-badge" title={t("chat.voiceBadgeLabel")} aria-label={t("chat.voiceBadgeLabel")}>🎤</span>}
                    {renderChatContent(m.content, profile?.name)}
                  </div>
                ))}
                {pendingVoiceText !== null && (
                  <div
                    className="chat-bubble user voice-pending-bubble"
                    dir="auto"
                    onClick={e => { if (!e.target.closest("button")) pendingVoiceTextareaRef.current?.focus(); }}
                  >
                    <textarea
                      ref={pendingVoiceTextareaRef}
                      rows={1}
                      className="voice-pending-input"
                      value={pendingVoiceText}
                      onFocus={beginEditingPendingVoiceText}
                      onChange={e => { beginEditingPendingVoiceText(); setPendingVoiceText(e.target.value); }}
                      aria-label={t("chat.voiceEditTranscript")}
                    />
                    {!pendingVoiceEditing && (
                      <div
                        key={pendingReviewId}
                        className="voice-pending-progress"
                        style={{ animationDuration: `${TRANSCRIPT_GRACE_PERIOD_MS}ms` }}
                      />
                    )}
                    <div className="voice-pending-actions">
                      <span className="voice-pending-hint">{t("chat.voiceReviewHint")}</span>
                      <button
                        type="button"
                        className="voice-pending-send"
                        onClick={() => confirmPendingVoiceText(pendingVoiceText)}
                        aria-label={t("chat.voiceSendNow")}
                        title={t("chat.voiceSendNow")}
                      >✓</button>
                      <button
                        type="button"
                        className="voice-pending-discard"
                        onClick={discardPendingVoiceText}
                        aria-label={t("chat.voiceDiscard")}
                        title={t("chat.voiceDiscard")}
                      >✕</button>
                    </div>
                  </div>
                )}
                {loading && <div className="chat-bubble assistant" dir="auto"><span className="chat-dots"><span/><span/><span/></span></div>}
              </>
            )}
          </div>
          {/* Fixed status/notification area, BELOW the scrollable conversation history rather than
              at the top of it — found (user report) that once a conversation has enough turns to
              overflow agent-chat-messages's scrollable box, the auto-scroll-to-bottom behavior
              leaves anything rendered at the TOP of that box (this was originally the first
              children of .agent-chat-messages) permanently out of view during an active voice
              turn, exactly when the user most needs to see "Listening…"/"Babi is speaking…" etc.
              This sits between the messages box and the input row — a sibling of both, not part
              of the scrolling area — so it's always visible regardless of scroll position or
              conversation length. Collapses to nothing (see .agent-chat-status-bar:empty in
              styles.js) when no banner condition below is active, rather than duplicating all six
              conditions into a second combined boolean that could drift out of sync with them. */}
          {voiceStage !== "consent" && (
            <div className="agent-chat-status-bar">
              {(voiceStage === "denied" || voiceStage === "device-in-use" || voiceStage === "error"
                || voiceStage === "not-entitled" || voiceStage === "no-balance" || voiceStage === "other-tab-active") && (
                <div className={`voice-banner voice-banner-${voiceStage}`} dir="auto">
                  <span>{t(
                    voiceStage === "denied" ? "chat.voiceDenied"
                      : voiceStage === "device-in-use" ? "chat.voiceDeviceInUse"
                      : voiceStage === "not-entitled" ? "chat.voiceNotEntitled"
                      : voiceStage === "no-balance" ? "chat.voiceNoBalance"
                      : voiceStage === "other-tab-active" ? "chat.voiceOtherTabActive"
                      : "chat.voiceError"
                  )}</span>
                  <button className="voice-banner-close" onClick={() => setVoiceStage("idle")} aria-label={t("chat.voiceBannerDismiss")}>✕</button>
                </div>
              )}
              {showWrapUpNudge && (
                <div className="voice-banner voice-banner-active" dir="auto">
                  <span>{t("chat.voiceLowBalanceNudge")}</span>
                  {canOfferVoiceOverage && (
                    <button type="button" className="voice-pending-send" onClick={() => onBuyVoiceMinutes?.()} title={t("chat.voiceGetMoreMinutes")} aria-label={t("chat.voiceGetMoreMinutes")}>＋</button>
                  )}
                  <button className="voice-banner-close" onClick={() => setShowWrapUpNudge(false)} aria-label={t("chat.voiceBannerDismiss")}>✕</button>
                </div>
              )}
              {/* A6: earlier, calmer heads-up than showWrapUpNudge above — appears well before
                  Babi's own 75-second wrap-up trigger, any time the widget is idle, so it's
                  checkable ahead of time rather than only shown reactively mid-turn. Suppressed
                  while showWrapUpNudge is already showing its own purchase button, to avoid two
                  "buy minutes" prompts on screen at once. */}
              {voiceStage === "idle" && canOfferVoiceOverage && !showWrapUpNudge && !lowMinutesBannerDismissed && (
                <div className="voice-banner voice-banner-active" dir="auto">
                  <span>{t("chat.voiceLowMinutesHeadsUp")}</span>
                  <button type="button" className="voice-pending-send" onClick={() => onBuyVoiceMinutes?.()} title={t("chat.voiceGetMoreMinutes")} aria-label={t("chat.voiceGetMoreMinutes")}>＋</button>
                  <button className="voice-banner-close" onClick={() => setLowMinutesBannerDismissed(true)} aria-label={t("chat.voiceBannerDismiss")}>✕</button>
                </div>
              )}
              {(voiceStage === "starting" || voiceStage === "capturing" || voiceStage === "processing" || voiceStage === "speaking") && (
                <div
                  className={`voice-banner voice-banner-active${
                    voiceStage === "starting" ? " voice-banner--starting"
                      : voiceStage === "capturing" ? " voice-banner--listening"
                      : ""
                  }`}
                  dir="auto"
                >
                  <span>{t(
                    voiceStage === "starting" ? "chat.voiceStarting"
                      : voiceStage === "capturing" ? "chat.voiceListening"
                      : voiceStage === "speaking" ? "chat.voiceSpeaking"
                      : "chat.voiceProcessing"
                  )}</span>
                </div>
              )}
              {voiceStage === "idle" && ttsNotice && (
                <div className="voice-banner voice-banner-empty" dir="auto">
                  {ttsNotice.type === "tap-to-play" ? (
                    <button
                      className="voice-tap-to-play-btn"
                      onClick={() => { ttsNotice.audio?.play().catch(() => {}); setTtsNotice(null); }}
                    >🔊 {t("chat.voiceTapToHear")}</button>
                  ) : (
                    <span>{t(
                      ttsNotice.type === "not-entitled" ? "chat.voiceNotEntitled"
                        : ttsNotice.type === "no-balance" ? "chat.voiceNoBalance"
                        : ttsNotice.type === "unavailable-language" ? "chat.voiceTtsUnavailableLanguage"
                        : "chat.voiceTtsUnavailable",
                      { language: profile?.baseLanguage || "" }
                    )}</span>
                  )}
                  <button className="voice-banner-close" onClick={() => setTtsNotice(null)} aria-label={t("chat.voiceBannerDismiss")}>✕</button>
                </div>
              )}
              {voiceStage === "idle" && transcriptState === "empty" && (
                <div className="voice-banner voice-banner-empty" dir="auto">
                  <span>{t("chat.voiceDidntCatch")}</span>
                  <button className="voice-banner-close" onClick={() => setTranscriptState("none")} aria-label={t("chat.voiceBannerDismiss")}>✕</button>
                </div>
              )}
            </div>
          )}
          <div className="agent-chat-input-row">
            {micSupported && (
              <button
                type="button"
                className={`agent-chat-mic-btn${voiceStage === "capturing" ? " agent-chat-mic-btn--active" : ""}${(!hasVoiceConsent || !voiceEntitled || !hasVoiceBalance) && voiceStage === "idle" ? " agent-chat-mic-btn--inactive" : ""}`}
                onClick={handleMicClick}
                disabled={["starting", "processing", "reviewing", "replying", "speaking"].includes(voiceStage)}
                aria-label={t(voiceStage === "capturing" ? "chat.voiceStopBtn" : "chat.voiceMicButtonLabel")}
                title={t(voiceStage === "capturing" ? "chat.voiceStopBtn" : "chat.voiceMicButtonLabel")}
              >{voiceStage === "capturing" ? "⏹" : "🎤"}</button>
            )}
            <input
              className="agent-chat-input"
              dir={input.trim() ? "auto" : (isRTL ? "rtl" : "ltr")}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && send()}
              placeholder={t("chat.placeholder")}
              disabled={loading}
            />
            <button className="agent-chat-send" onClick={() => send()} disabled={loading || !input.trim()}>{t("chat.sendBtn")}</button>
          </div>
        </div>
      )}
      <button className="agent-chat-toggle" ref={toggleRef} onClick={() => setOpen(o => !o)}>
        <img src="/babi%20fantasy2.jpg?v=2" style={{width:28,height:28,borderRadius:"50%",objectFit:"cover"}} alt="Babi" />
        <span>{t("chat.askBabi")}</span>
        <span style={{fontSize:"0.6875rem",opacity:0.8}}>{open ? "▼" : "▲"}</span>
      </button>
    </div>
  );
}
